import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useStars, STARS, CELEBRATED_KEY } from "../lib/stars";
import { StarBurst } from "../components/StarBurst";
import { Bini } from "../components/Bini";
import { StarShape } from "../components/HiddenStar";
import { Layout } from "../components/Layout";
import { t } from "../i18n";

const Rainbow = () => (
  <div className="absolute left-1/2 top-[58%] h-72 w-[34rem] -translate-x-1/2 rounded-t-full opacity-80"
    style={{ background: "radial-gradient(circle at 50% 100%, transparent 50%, #8A3FE0 50%, #8A3FE0 56%, #38B6FF 56%, #38B6FF 62%, #4CC417 62%, #4CC417 68%, #FFD21F 68%, #FFD21F 74%, #FF8A1F 74%, #FF8A1F 80%, #F0303A 80%, #F0303A 86%, transparent 86%)" }} />
);

const DECOR = [
  { at: 1, key: "moon", el: <div className="absolute right-[8%] top-[8%] h-20 w-20 rounded-full bg-[#FFF3B0] shadow-[0_0_40px_rgba(255,243,176,0.8)]" /> },
  { at: 2, key: "rainbow", el: <Rainbow /> },
  { at: 3, key: "clouds", el: <><div className="cloud-shape" style={{ width: 150, height: 40, left: "6%", top: "70%" }} /><div className="cloud-shape" style={{ width: 120, height: 34, right: "8%", top: "78%" }} /></> },
  { at: 4, key: "bubbles", el: <>{[[12, 40, 40], [78, 30, 30], [64, 82, 50]].map(([x, y, s]) => <div key={x} className="bubble anim-float" style={{ left: `${x}%`, top: `${y}%`, width: s, height: s }} />)}</> },
  { at: 5, key: "planet", el: <div className="absolute left-[8%] top-[34%] h-14 w-14 rounded-full bg-[#FF5FA8] shadow-[inset_-6px_-6px_0_rgba(0,0,0,0.12)]"><div className="absolute left-[-30%] top-[40%] h-3 w-[160%] rounded-full border-2 border-[#FFD21F] rotate-[-15deg]" /></div> },
  { at: 6, key: "crown", el: null },
];

export default function BiniSky() {
  const { found, total } = useStars();
  const n = found.length;
  const complete = n >= total;
  const [party, setParty] = useState(0);
  const msg = party ? t("stars.party") : n === 0 ? t("stars.skyEmpty") : n < total ? t("stars.skySome") : t("stars.skyFull");

  useEffect(() => {
    if (!complete || localStorage.getItem(CELEBRATED_KEY)) return;
    localStorage.setItem(CELEBRATED_KEY, "1");
    setParty(Date.now());
  }, [complete]);

  useEffect(() => {
    if (!party) return;
    const id = setTimeout(() => setParty(0), 3200);
    return () => clearTimeout(id);
  }, [party]);

  return (
    <Layout back="/">
      <h1 className="text-4xl sm:text-5xl font-extrabold text-white [text-shadow:0_3px_0_rgba(30,42,90,0.45),0_0_14px_rgba(30,42,90,0.45)]">{t("stars.skyTitle")}</h1>
      <section data-testid="bini-sky" data-found={n}
        className="relative mt-6 h-[480px] sm:h-[560px] overflow-hidden rounded-[40px] border-4 border-white shadow-2xl"
        style={{ background: `linear-gradient(180deg, ${n >= 3 ? "#5B3FC4" : "#2A3B7A"} 0%, #38B6FF 100%)` }}>
        {DECOR.filter((d) => n >= d.at && d.el).map((d) => <div key={d.key} data-testid={`sky-decor-${d.key}`} className="anim-pop">{d.el}</div>)}
        {STARS.map((s) => {
          const on = found.includes(s.id);
          return (
            <div key={s.id} data-testid={`sky-star-${s.id}`} data-found={on} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${s.x}%`, top: `${s.y}%` }}>
              {on ? <StarShape color={s.color} className="h-14 w-14 anim-pop drop-shadow-[0_0_12px_rgba(255,255,255,0.8)]" />
                : <div className="h-10 w-10 rounded-full border-2 border-dashed border-white/40" />}
            </div>
          );
        })}
        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center">
          <div className="relative">
            {party > 0 && <StarBurst key={party} />}
            {complete && <StarShape color="#FFD21F" className="absolute -top-8 left-1/2 h-10 w-10 -translate-x-1/2 anim-wiggle" />}
            {complete ? (
              <button type="button" onClick={() => setParty(Date.now())} data-testid="sky-bini-party-btn" data-party={party > 0}
                aria-label={t("stars.partyAgain")} className="block">
                <div key={party} className={party ? "party-jump" : "anim-float"}><Bini size={110} /></div>
              </button>
            ) : <Bini size={110} className="anim-float" />}
          </div>
        </div>
      </section>
      <div className="speech mt-6 text-xl font-semibold" data-testid="bini-sky-message">{msg}</div>
      <Link to="/" data-testid="sky-home-btn" className="btn-3d mt-6 w-full sm:w-auto" style={{ "--c": "#38B6FF" }}>{t("soon.home")}</Link>
    </Layout>
  );
}
